import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();
  const [theme, setTheme] = useState('dark');
  const [isDark, setIsDark] = useState(true);

  // Initialize theme from user preference or localStorage
  useEffect(() => {
    const initializeTheme = () => {
      let initialTheme = 'dark'; // Default to dark mode
      
      // Priority: 1. User preference from backend, 2. localStorage, 3. dark default
      if (user?.theme_preference) {
        initialTheme = user.theme_preference;
      } else {
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme && ['light', 'dark', 'system'].includes(savedTheme)) {
          initialTheme = savedTheme;
        }
      }
      
      setTheme(initialTheme);
      applyTheme(initialTheme);
    };

    initializeTheme();
  }, [user]);

  // Apply theme to document
  const applyTheme = (themeValue) => {
    const root = document.documentElement;
    
    if (themeValue === 'dark') {
      root.classList.add('dark');
      setIsDark(true);
    } else if (themeValue === 'light') {
      root.classList.remove('dark');
      setIsDark(false);
    } else { // system
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark) {
        root.classList.add('dark');
        setIsDark(true);
      } else {
        root.classList.remove('dark');
        setIsDark(false);
      }
    }
  };

  // Change theme and persist to backend if user is logged in
  const changeTheme = async (newTheme) => {
    setTheme(newTheme);
    applyTheme(newTheme);
    
    // Save to localStorage
    localStorage.setItem('theme', newTheme);
    
    // Save to backend if user is logged in
    if (user) {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'https://api.air-brief.com';
        await fetch(`${API_URL}/api/auth/profile/`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('atpl_access_token')}`
          },
          body: JSON.stringify({ theme_preference: newTheme })
        });
      } catch (error) {
        // Failed to save theme preference
      }
    }
  };

  // Listen for system theme changes
  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => applyTheme('system');
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  const value = {
    theme,
    isDark,
    changeTheme,
    applyTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
